import React, { useEffect, useMemo, useState } from "react";
import { Users } from "lucide-react";

// Next September 1st, 09:00 local time (rolls over to next year once passed)
function autoBatchStart() {
  const now = new Date();
  const year = now.getFullYear();
  let target = new Date(year, 8, 1, 9, 0, 0); // month 8 = September
  if (now > target) target = new Date(year + 1, 8, 1, 9, 0, 0);
  return target;
}

function resolveTarget(startDate) {
  if (startDate) {
    const d = new Date(`${startDate}T09:00:00`);
    if (!isNaN(d.getTime())) return d;
  }
  return autoBatchStart();
}

function diff(target) {
  const total = Math.max(0, target - new Date());
  return {
    total,
    days: Math.floor(total / 86400000),
    hours: Math.floor((total / 3600000) % 24),
    minutes: Math.floor((total / 60000) % 60),
    seconds: Math.floor((total / 1000) % 60),
  };
}

export default function BatchCountdown({ startDate, seatsLeft }) {
  const target = useMemo(() => resolveTarget(startDate), [startDate]);
  const [t, setT] = useState(() => diff(target));

  useEffect(() => {
    setT(diff(target));
    const id = setInterval(() => setT(diff(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  const monthLabel = target.toLocaleString("en-US", { month: "long", year: "numeric" });
  const units = [
    { k: "days", v: t.days, l: "Days" },
    { k: "hours", v: t.hours, l: "Hrs" },
    { k: "minutes", v: t.minutes, l: "Min" },
    { k: "seconds", v: t.seconds, l: "Sec" },
  ];

  return (
    <div
      data-testid="batch-countdown"
      className="mt-8 inline-flex flex-col gap-3 rounded-2xl bg-white/5 backdrop-blur-md border border-yellow-400/25 p-4 sm:p-5 animate-fade-up"
      style={{ animationDelay: "0.4s" }}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-yellow-300">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-70 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-400" />
          </span>
          Next Batch Starts In
        </div>
        {seatsLeft > 0 && (
          <span
            data-testid="seats-left-badge"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/90 text-white text-[11px] font-black uppercase tracking-wider animate-pulse"
          >
            <Users className="h-3 w-3" />
            Only {seatsLeft} seats left
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        {units.map((u) => (
          <div key={u.k} className="flex flex-col items-center">
            <div
              data-testid={`countdown-${u.k}`}
              className="min-w-[3.25rem] sm:min-w-[3.75rem] rounded-lg bg-[#0B2A6B]/60 border border-white/10 px-2 py-2 text-center text-2xl sm:text-3xl font-black text-yellow-400 tabular-nums shadow-inner"
            >
              {String(u.v).padStart(2, "0")}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-300">{u.l}</div>
          </div>
        ))}
      </div>
      <div className="text-xs text-slate-300">
        Admissions close soon — seats fill up for <span className="text-yellow-300 font-semibold">{monthLabel}</span>.
      </div>
    </div>
  );
}
